import React, { useState, useMemo } from "react";
import PageHeader from "../components/PageHeader";
import SectionTitle from "../components/SectionTitle";
import * as Icons from "lucide-react";
import {
  MapPin, Clock, Briefcase, Calendar, ChevronRight, ChevronDown, Upload, Send, CheckCircle2, X,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { submitApplication } from "../api/services";
import { useCareerOpenings, useCareersPage } from "../api/queries";
import HtmlContent from "../components/HtmlContent";
import { type } from "../lib/typography";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function JobRow({ job, onApply }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-slate-200 hover:border-[#1e6fcf] hover:shadow-lg transition-all duration-300 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-6 py-5 flex flex-col md:flex-row md:items-center justify-between gap-4 text-left"
      >
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className={`${type.cardTitle} text-[#0a1a2f]`}>
              {job.title}
            </h3>
            <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded bg-[#e8f2fc] text-[#1758a8]">
              {job.department}
            </span>
          </div>
          <div className={`mt-3 flex flex-wrap gap-x-5 gap-y-2 ${type.meta} text-slate-600`}>
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#1e6fcf]" /> {job.location}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#1e6fcf]" /> {job.type}</span>
            <span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-[#1e6fcf]" /> {job.experience}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[#1e6fcf]" /> Posted {formatDate(job.posted)}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span
            onClick={(e) => {
              e.stopPropagation();
              onApply(job);
            }}
            role="button"
            tabIndex={0}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-[#0a1a2f] shadow-sm transition-all duration-300 hover:border-[#1e6fcf] hover:bg-[#1e6fcf] hover:text-white cursor-pointer"
          >
            Apply Now <ChevronRight className="w-4 h-4" />
          </span>
          <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="px-6 pb-6 border-t border-slate-100 pt-5 animate-fade-up">
          <h4 className={`${type.label} text-[#1758a8] mb-3`}>About this role</h4>
          <HtmlContent html={job.summary} className={`${type.bodySm} text-slate-700`} />
          {job.responsibilities?.length > 0 && (
            <div className="mt-5">
              <h4 className={`${type.label} text-[#1758a8] mb-3`}>Responsibilities</h4>
              <ul className="space-y-2">
                {job.responsibilities.map((r) => (
                  <li key={r} className={`flex items-start gap-2.5 ${type.bodySm} text-slate-700`}>
                    <CheckCircle2 className="w-4 h-4 text-[#1e6fcf] flex-shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {job.requirements?.length > 0 && (
            <div className="mt-5">
              <h4 className={`${type.label} text-[#1758a8] mb-3`}>Requirements</h4>
              <ul className="space-y-2">
                {job.requirements.map((r) => (
                  <li key={r} className={`flex items-start gap-2.5 ${type.bodySm} text-slate-700`}>
                    <CheckCircle2 className="w-4 h-4 text-[#1e6fcf] flex-shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ApplicationModal({ job, onClose }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", experience: "", currentCompany: "",
    coverLetter: "", resumeName: "",
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setResumeFile(f);
      setForm((p) => ({ ...p, resumeName: f.name }));
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitApplication({
        ...form,
        jobId: job.id,
        jobTitle: job.title,
        resumeFile,
      });
      setSubmitted(true);
      toast({
        title: "Application submitted",
        description: `Thank you for applying to ${job.title}. Our team will review and reach out.`,
      });
      setTimeout(() => onClose(), 1500);
    } catch {
      toast({
        title: "Submission failed",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0a1a2f]/70 backdrop-blur-sm animate-fade-up">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#1758a8]">Application Form</div>
            <h3 className={`${type.cardTitle} text-[#0a1a2f]`}>{job.title}</h3>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-md bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {submitted ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h4 className={`mt-5 ${type.sectionTitle} text-[#0a1a2f]`}>Application Received!</h4>
            <p className="mt-2 text-slate-600">Thank you for your interest in SSA Techno. Our HR team will review your application and get back to you soon.</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="p-6 grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-1">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Full Name *</label>
              <input required name="name" value={form.name} onChange={onChange}
                className="mt-2 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e6fcf] focus:border-transparent transition-all"
                placeholder="Your full name" />
            </div>
            <div className="sm:col-span-1">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Email *</label>
              <input required type="email" name="email" value={form.email} onChange={onChange}
                className="mt-2 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e6fcf] focus:border-transparent transition-all"
                placeholder="you@example.com" />
            </div>
            <div className="sm:col-span-1">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Phone *</label>
              <input required name="phone" value={form.phone} onChange={onChange}
                className="mt-2 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e6fcf] focus:border-transparent transition-all"
                placeholder="+91 9XXXX XXXXX" />
            </div>
            <div className="sm:col-span-1">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Total Experience</label>
              <input name="experience" value={form.experience} onChange={onChange}
                className="mt-2 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e6fcf] focus:border-transparent transition-all"
                placeholder="e.g. 4 years" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Current Company</label>
              <input name="currentCompany" value={form.currentCompany} onChange={onChange}
                className="mt-2 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e6fcf] focus:border-transparent transition-all"
                placeholder="Where do you currently work?" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Cover Letter</label>
              <textarea rows={4} name="coverLetter" value={form.coverLetter} onChange={onChange}
                className="mt-2 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e6fcf] focus:border-transparent transition-all resize-none"
                placeholder="Tell us why you would be a great fit..." />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Resume / CV *</label>
              <label className="mt-2 flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 border-2 border-dashed border-slate-300 hover:border-[#1e6fcf] rounded-md cursor-pointer transition-all">
                <span className="flex items-center gap-3 text-sm text-slate-600">
                  <Upload className="w-5 h-5 text-[#1e6fcf]" />
                  {form.resumeName || "Upload PDF or DOC (max 5MB)"}
                </span>
                <span className="text-xs font-bold uppercase tracking-wider text-[#1e6fcf]">Browse</span>
                <input required type="file" accept=".pdf,.doc,.docx" onChange={onFile} className="hidden" />
              </label>
            </div>
            <div className="sm:col-span-2 flex items-center justify-end gap-3 pt-3">
              <button type="button" onClick={onClose}
                className="px-5 py-3 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-md transition-colors">
                Cancel
              </button>
              <button type="submit"
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-[#0a1a2f] shadow-sm transition-all duration-300 hover:border-[#1e6fcf] hover:bg-[#1e6fcf] hover:text-white">
                Submit Application
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function CareersPage() {
  const [selectedJob, setSelectedJob] = useState(null);
  const [filter, setFilter] = useState("All");
  const { data: careersPage } = useCareersPage();
  const { data: allJobs = [] } = useCareerOpenings();

  const pageHeader = careersPage?.header ?? null;
  const benefits = careersPage?.benefits ?? [];

  const jobOpenings = useMemo(() => {
    if (filter === "All") return allJobs;
    return allJobs.filter((job) => job.department === filter);
  }, [allJobs, filter]);

  const departments = useMemo(() => {
    const set = new Set(allJobs.map((j) => j.department));
    return ["All", ...Array.from(set)];
  }, [allJobs]);

  return (
    <>
      <PageHeader
        title={pageHeader?.title || "Careers"}
        breadcrumb={pageHeader?.title || "Careers"}
        image={pageHeader?.bannerImageUrl}
      />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTitle
            align="center"
            eyebrow={pageHeader?.tagline || "At SSA Techno"}
            title={pageHeader?.title}
            subtitle={pageHeader?.description}
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b) => {
              const Icon = Icons[b.icon] || Icons.Briefcase;
              return (
                <div key={b.id || b.title} className="group p-7 bg-slate-50 rounded-xl border border-transparent hover:border-[#1e6fcf] hover:bg-white hover:shadow-xl transition-all duration-500">
                  <div className="w-14 h-14 rounded-lg bg-[#1e6fcf]/10 group-hover:bg-[#1e6fcf] flex items-center justify-center transition-all">
                    <Icon className="w-7 h-7 text-[#1e6fcf] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className={`mt-5 ${type.subsectionTitle} text-[#0a1a2f]`}>{b.title}</h3>
                  <HtmlContent html={b.desc} className="mt-2 text-sm text-slate-600" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <SectionTitle
              eyebrow="Current Openings"
              title="We Are Hiring"
            />
            <div className="flex flex-wrap gap-2">
              {departments.map((d) => (
                <button
                  key={d}
                  onClick={() => setFilter(d)}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-md transition-all ${
                    filter === d
                      ? "bg-[#1e6fcf] text-white shadow-md"
                      : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {jobOpenings.map((j) => (
              <JobRow key={j.id} job={j} onApply={setSelectedJob} />
            ))}
            {jobOpenings.length === 0 && (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <Briefcase className="w-12 h-12 text-slate-300 mx-auto" />
                <p className="mt-4 text-slate-500">No open positions in this department right now.</p>
              </div>
            )}
          </div>

          <div className="mt-12 p-8 bg-gradient-to-r from-[#0a1a2f] to-[#0f2747] rounded-2xl text-white flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className={`${type.cardTitle} text-white`}>Don't see your role?</h3>
              <p className="mt-2 text-slate-300 text-sm">We're always open to talented engineers. Reach out and we'll be in touch when a fit opens up.</p>
            </div>
            <button
              type="button"
              onClick={() =>
                setSelectedJob({
                  id: "general-application",
                  title: "General Application",
                  department: "Open Application",
                })
              }
              className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-5 py-3 text-sm font-bold text-white transition-all duration-300 hover:border-[#1e6fcf] hover:bg-[#1e6fcf] flex-shrink-0"
            >
              Send Your CV
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {selectedJob && (
        <ApplicationModal job={selectedJob} onClose={() => setSelectedJob(null)} />
      )}
    </>
  );
}
